import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, YStack } from "tamagui";
import { toast } from "sonner";

import AuthScaffold from "@/components/AuthScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { trackKindredProductEvent } from "@/lib/productAnalytics";
import { consumeWebPostAuthRedirect } from "@/lib/postAuthRedirect";
import { notifySignupAutomation } from "@/lib/signupWebhook";
import {
  consumeWebSocialAuthContext,
  getWebSocialAuthOutcome,
  getWebSocialAuthRedirectError,
} from "@/lib/socialAuth";
import { AppText } from "../../ui-foundation";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { user, loading, securityLoading } = useAuth();
  const hasHandled = useRef(false);

  useEffect(() => {
    if (hasHandled.current || loading || securityLoading) {
      return;
    }

    const callbackError = getWebSocialAuthRedirectError();
    if (callbackError && !user) {
      hasHandled.current = true;
      toast.error(callbackError);
      navigate("/signin", { replace: true });
      return;
    }

    hasHandled.current = true;

    void (async () => {
      const {
        data: { session },
      } = user ? { data: { session: { user } } } : await supabase.auth.getSession();
      const signedInUser = user ?? session?.user ?? null;

      if (!signedInUser) {
        toast.error("Kindred couldn't finish that sign-in. Please try again.");
        navigate("/signin", { replace: true });
        return;
      }

      const authContext = consumeWebSocialAuthContext();
      const outcome = await getWebSocialAuthOutcome(signedInUser);

      if (authContext?.intent === "signup" && authContext.provider && outcome.looksNewUser && signedInUser.email) {
        void notifySignupAutomation({
          userId: signedInUser.id,
          email: signedInUser.email,
          displayName:
            authContext.fallbackDisplayName ||
            signedInUser.user_metadata?.display_name ||
            signedInUser.user_metadata?.full_name ||
            signedInUser.email.split("@")[0] ||
            null,
          signupMethod: authContext.provider,
          platform: "web",
        });
      }

      await trackKindredProductEvent({
        userId: signedInUser.id,
        eventName: "sign_in_success",
        screenName: "sign_in",
        metadata: { method: authContext?.provider ?? "social" },
      });
      navigate(outcome.needsOnboarding ? "/onboarding" : consumeWebPostAuthRedirect(), { replace: true });
    })().catch((error) => {
      console.error("Error finishing social auth callback:", error);
      toast.error(error instanceof Error ? error.message : "Kindred couldn't finish that sign-in.");
      navigate("/signin", { replace: true });
    });
  }, [loading, navigate, securityLoading, user]);

  return (
    <AuthScaffold
      eyebrow="Almost there"
      title="Finishing your sign-in."
      description="Kindred is safely bringing your account back into the app."
    >
      <YStack alignItems="center" gap="$sm" paddingVertical="$xl">
        <Spinner color="$primary" size="large" />
        <AppText color="$colorMuted" textAlign="center">
          Checking your account and sending you to the right next step.
        </AppText>
      </YStack>
    </AuthScaffold>
  );
};

export default AuthCallback;
